import { adoptEntity } from '@/backend/adoptEntity'
import { toast } from '@/shared/toast'
import { nonNullable } from '@/shared/utils/typeAssertions'
import { useWebSocketTopic } from '@/sharedComposables/useWebSocketTopic'
import { until } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { serializeCase } from './serializers'
import type { Case } from './types'
import { useAgentPicker } from './useAgentPicker'
import { useCaseBackend } from './useCaseBackend'
import { useCasePendingQueryStore } from './useCasePendingQueryStore'
import { useCaseStore } from './useCaseStore'
import { useCaseUploadStore } from './useCaseUploadStore'

/**
 * Single source of truth for creation of a new case.
 *
 * The intent is to allow picking files and an agent,
 * but delay doing any case creation until the input form is submitted.
 */
export const useNewCase = (opts: { showsStatusToast?: boolean } = {}) => {
  const router = useRouter()

  const { agents, agentId } = useAgentPicker()

  const { createCase, deleteCase } = useCaseBackend()

  const uploadStore = useCaseUploadStore()

  const caseStore = useCaseStore()

  const statusText = ref<string>()

  const hubId = ref<string>()

  const CASE_TOAST_ID = 'USE-NEW-CASE-TOAST'
  const setStatusText = (text?: string) => {
    statusText.value = text
    if (opts.showsStatusToast && text) toast.loading(text, { id: CASE_TOAST_ID })
    if (opts.showsStatusToast && !text) toast.dismiss(CASE_TOAST_ID)
  }

  const messageText = ref('')
  const webSearch = ref(false)

  const pendingQueriesStore = useCasePendingQueryStore()

  const newCase = ref<Case>()

  const startNewCase = async (props: {
    workspaceId: string
    initialMessage: string
    entities?: { projectId: string; entityId: string }[]
    /** Determine which router method to use for navigation. Defaults to 'push'. */
    navigate: 'push' | 'replace' | 'none'
  }) => {
    newCase.value = undefined

    setStatusText('Creating empty case')

    const res = await createCase({ workspaceId: props.workspaceId })

    if (!res.ok) {
      setStatusText(undefined)
      toast.error('Failed to create a case, please contact support if this issue persists.')
      return
    }

    newCase.value = serializeCase(res.data)
    const caseId = newCase.value.id
    uploadStore.attachUnlinkedUploadsToCase(caseId)

    if (props.entities && props.entities.length > 0) {
      const topic = ref(`case:${props.workspaceId}:${caseId}` as const)
      const { subscribe, isSubscribed } = useWebSocketTopic(topic)

      await until(() => isSubscribed.value).toBe(true)

      subscribe('case:updated', (data) => {
        newCase.value = serializeCase(data.case)
      })
      // every entity adoption call returns the updated case. the case briefly becomes "not ready"
      // which will cause subsequent adoption calls or queries to fail
      // thus, after each adoption call, we must wait until the case is ready
      for (let i = 0; i < props.entities.length; i++) {
        setStatusText(`Importing ${i + 1} of ${props.entities.length}`)
        const { entityId, projectId } = nonNullable(props.entities[i])
        const res = await adoptEntity({
          workspaceId: props.workspaceId,
          caseId,
          entityId,
          projectId,
        })

        if (!res.ok) {
          toast.error(
            'One or more entities failed to adopt, please contact support if this issue persists.',
          )
          setStatusText(undefined)
          await deleteCase({ workspaceId: props.workspaceId, caseId })
          return
        }

        newCase.value = serializeCase(res.data)

        await until(() => newCase.value?.status === 'ready').toBe(true)
      }
    }

    caseStore.setCase(newCase.value)
    setStatusText('Opening case')
    if (props.navigate === 'push') {
      await router.push({ name: 'Case', params: { caseId }, query: { hubId: hubId.value } })
    } else if (props.navigate === 'replace') {
      await router.replace({ name: 'Case', params: { caseId }, query: { hubId: hubId.value } })
    }

    // this query will be sent as soon as
    // - the case itself is ready (no adopted entities, or all done with adoption)
    // - all selected files are uploaded and processed
    // useQueryDispatcher will take care of sending the query when these conditions are met
    pendingQueriesStore.addPendingQuery({
      caseId,
      text: props.initialMessage,
      agentId: agentId.value,
      hubId: hubId.value,
      uploadIds: uploadStore.uploads.filter((u) => u.caseId === caseId).map((u) => u.id),
      type: 'create',
      webSearch: webSearch.value,
    })

    setStatusText(undefined)

    return caseId
  }

  const attachments = computed(() => uploadStore.attachmentsForCase(null))

  const isSendable = computed(
    () =>
      statusText.value === undefined &&
      (messageText.value.length > 0 || attachments.value.length > 0),
  )

  return {
    addIntegrationFiles: uploadStore.addIntegrationFiles,
    addLibraryFiles: uploadStore.addLibraryFiles,
    addPendingUploads: uploadStore.addPendingUploads,
    agentId,
    agents,
    attachments,
    messageText,
    removeUpload: uploadStore.removeUpload,
    startNewCase,
    statusText,
    webSearch,
    isSendable,
    hubId,
  }
}
